import { isMuted, setMuted, setMusicEnabled } from './audio';

const KEYS = {
  music: 'shmerge.settings.music',
  shake: 'shmerge.settings.shake',
  tilt: 'shmerge.settings.tilt',
  tiltDeviceMode: 'shmerge.settings.tiltDeviceMode',
} as const;

export interface Settings {
  sfx: boolean;
  music: boolean;
  shake: boolean;
  tilt: boolean;
  tiltDeviceMode: boolean;
}

export function isTouchDevice(): boolean {
  if (typeof window === 'undefined') return false;
  return 'ontouchstart' in window || (navigator?.maxTouchPoints ?? 0) > 0;
}

export function hasDeviceOrientation(): boolean {
  return isTouchDevice() && typeof DeviceOrientationEvent !== 'undefined';
}

type Listener = (s: Settings) => void;
const listeners = new Set<Listener>();

function readBool(key: string, def: boolean): boolean {
  try {
    const v = localStorage.getItem(key);
    if (v === null) return def;
    return v === '1';
  } catch {
    return def;
  }
}

function writeBool(key: string, value: boolean): void {
  try {
    localStorage.setItem(key, value ? '1' : '0');
  } catch {}
}

export function getSettings(): Settings {
  return {
    sfx: !isMuted(),
    music: readBool(KEYS.music, true),
    shake: readBool(KEYS.shake, false),
    tilt: readBool(KEYS.tilt, false),
    // Default: device orientation on mobile, joystick on desktop.
    tiltDeviceMode: readBool(KEYS.tiltDeviceMode, isTouchDevice()),
  };
}

export function setSetting(name: keyof Settings, on: boolean): Settings {
  switch (name) {
    case 'sfx':
      setMuted(!on);
      break;
    case 'music':
      writeBool(KEYS.music, on);
      setMusicEnabled(on);
      break;
    case 'shake':
      writeBool(KEYS.shake, on);
      break;
    case 'tilt':
      writeBool(KEYS.tilt, on);
      break;
    case 'tiltDeviceMode':
      writeBool(KEYS.tiltDeviceMode, on);
      break;
  }
  const snap = getSettings();
  for (const l of listeners) l(snap);
  return snap;
}

export function onSettingsChange(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function isShakeEnabled(): boolean {
  return readBool(KEYS.shake, false);
}

export function isTiltEnabled(): boolean {
  return readBool(KEYS.tilt, false);
}

export function isTiltDeviceModeEnabled(): boolean {
  return readBool(KEYS.tiltDeviceMode, isTouchDevice());
}

/** True when tilt is on and the joystick wheel should be visible. */
export function isTiltJoystickActive(): boolean {
  return isTiltEnabled() && !isTiltDeviceModeEnabled();
}
